
import { fromJS, List, Map } from 'immutable';

import { setProductErrors, updateProductVariationsErrors } from './errors';
import { filterOperations, replaceOperation } from './operations';
import { filterNewTags, tagsFromArray } from '../tags';
import { areComplete, setIsComplete } from './isComplete';
import { getOperationsFromProduct } from './getOperationsFromProduct';
import { shapeVariationsForApp } from '../variations/shapeForApp';
import { editVariations } from '../edit/variations';
import { newVariations } from '../new/product';
import { editDetails } from '../edit/details';
import { editTags } from '../edit/tags';
import { shapeId } from '../listings/listings';
import { getSize } from '../iterable/getSize';
import { convert } from './convert';
import { retain } from '../variations/retain';

import { CATEGORY, NEW_TAGS, TAGS, VARIATIONS } from '../../constants/attributes';
import { ERROR, OPERATIONS, VALUE } from '../../constants/product';
import { MAX_NUMBER_OF_TAGS } from '../../constants/validations';
import { FORMS, PROFILE } from '../../constants/profiles';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { COMBINATIONS } from '../../constants/variations';
import { EMPTY, VELA } from '../../constants';
import { FORM } from '../../constants/forms';

export function applyProfile({ channel, data, fromVelaForm, profileId, shopId, state, type }) {
  function reduceChannelsForTags(velaTags) {
    return function reduceChannels(productsByShopId, shopIds, shopsChannel) {
      const tags = convert({
        from: channel,
        to: shopsChannel,
        type: TAGS,
        value: velaTags.getIn(['currentProfile', TAGS]),
      });

      const currentProfile = Map({ [TAGS]: tags });

      function reduceShopIds(result, productShopId) {
        function updateProduct(source) {
          const product = source
            .deleteIn([TAGS, NEW_TAGS])
            .setIn([TAGS, VALUE], EMPTY.ORDERED_MAP)
            .setIn([TAGS, 'currentProfile'],
              productShopId === shopId
                ? currentProfile.set('profileId', velaTags.getIn(['currentProfile', 'profileId']))
                : currentProfile
            );

          const freeSpace = MAX_NUMBER_OF_TAGS[shopsChannel] - getSize(tags);
          const newTags = freeSpace > 0
            ? velaTags
              .get(NEW_TAGS)
              .filter(filterNewTags(currentProfile.get(TAGS)))
              .slice(0, freeSpace)
            : EMPTY.ORDERED_MAP;

          const updates = editTags({
            channel: shopsChannel,
            newTags,
            operation: OPERATIONS[channel].TAGS.CHANGE_TO,
            product,
          });

          return setIsComplete(
            updates.product.set('operations',
              updates.operations.reduce(
                replaceOperation,
                updates.product.get('operations', EMPTY.LIST)
              )
            )
          );
        }

        return result.update(productShopId, updateProduct);
      }

      return shopIds.reduce(reduceShopIds, productsByShopId);
    };
  }

  function reduceChannelsForVariations({ category, variations }) {
    return function reduceChannels(interim, shopIds, shopsChannel) {
      function reduceShopIds(result, productShopId) {
        let product = result.propducts.get(productShopId);

        if (channel === ETSY) {
          if (category !== product.getIn([FORM.DETAILS, CATEGORY, VALUE])) {
            if (!product.getIn([FORM.DETAILS, CATEGORY, 'oldCategory'])) {
              product = product.setIn([FORM.DETAILS, CATEGORY, 'oldCategory'],
                product.getIn([FORM.DETAILS, CATEGORY, VALUE])
              );
            }

            const updates = editDetails({ channel, product, [CATEGORY]: { category }});

            if (updates.confirmation) {
              result.confirmation = updates.confirmation;
            }

            product = updates.product;
          }
        }

        const updates = editVariations({
          channel: shopsChannel,
          product: product.set(VARIATIONS, newVariations({ channel: shopsChannel })),
          updateCategory: channel === ETSY,
          value: convert({
            from: channel,
            category: product.getIn([FORM.DETAILS, CATEGORY, VALUE]),
            inventory: product.get(FORM.INVENTORY),
            to: shopsChannel,
            type: VARIATIONS,
            value: variations.delete('oldVariations').delete('currentProfile'),
          }),
        });

        product = setIsComplete(
          updates.product.set('operations',
            updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
          )
        );

        result.propducts = result.propducts.set(productShopId, product);
      }

      return shopIds.reduce(reduceShopIds, interim);
    };
  }

  if (fromVelaForm) {
    switch (type) {
      case PROFILE.TAGS: {
        const currentProfile = Map({ profileId, shopId, [TAGS]: tagsFromArray(data.profile.tags) });
        const tags = Map({
          currentProfile,
          [NEW_TAGS]: state
            .getIn([VELA, TAGS, NEW_TAGS], EMPTY.ORDERED_MAP)
            .filter(filterNewTags(currentProfile.get(TAGS))),
        });

        const products = state
          .get('channels')
          .reduce(reduceChannelsForTags(tags), state.get('products'));

        return {
          state: state
            .setIn([VELA, TAGS], tags)
            .set('products', products)
            .set('isComplete', areComplete(products)),
        };
      }

      case PROFILE.VARIATIONS: {
        const { profile: { title, value }} = data;
        const { taxonomyId: category } = value;
        const product = state.get(VELA, EMPTY.MAP);
        const oldVariations = product.getIn([FORM.VARIATIONS, 'oldVariations'], product.get(VARIATIONS));
        const photos = product.get(FORM.PHOTOS);
        const inventory = product.get(FORM.INVENTORY);
        const variations = shapeVariationsForApp({ category, channel, data: value, inventory, photos })
          .set('oldVariations', oldVariations)
          .set('currentProfile', Map({ profileId, shopId, title }));

        const { confirmation, products } = state
          .get('channels')
          .reduce(reduceChannelsForVariations({ category, variations }), { products: state.get('products') });

        return {
          confirmation,
          state: state
            .set(VELA, product.set(VARIATIONS, variations))
            .set('products', products)
            .set('isComplete', areComplete(products)),
        };
      }

      default: {
        return { state };
      }
    }
  }

  let confirmation;
  let product = state.getIn(['products', shopId]);
  const operations = product.get('operations', EMPTY.LIST);

  function splitOperationsByType(pattern) {
    return function reduceOperations(result, operation) {
      if (pattern.test(operation.get('type'))) {
        result.archivedOperations = result.archivedOperations.push(operation);
      } else {
        result.filteredOperations = result.filteredOperations.push(operation);
      }

      return result;
    };
  }

  switch (type) {
    case PROFILE.TAGS: {
      const profile = Map({
        profileId: shapeId(data.profile.id),
        [TAGS]: tagsFromArray(data.profile.tags),
      });

      product = product.setIn([TAGS, 'currentProfile'], profile);

      const freeSpace = MAX_NUMBER_OF_TAGS[channel] - getSize(profile.get(TAGS));
      const newTags = freeSpace > 0
        ? product
          .getIn([TAGS, NEW_TAGS], EMPTY.ORDERED_MAP)
          .filter(filterNewTags(profile.get(TAGS)))
          .slice(0, freeSpace)
        : EMPTY.ORDERED_MAP;

      const updates = editTags({
        channel,
        newTags,
        operation: OPERATIONS[channel].TAGS.CHANGE_TO,
        product,
      });

      product = updates.product.set('operations',
        operations
          .filter(filterOperations(OPERATIONS[channel].TAGS.CHANGE_TO, false))
          .concat(List(updates.operations))
      );

      break;
    }

    case PROFILE.VARIATIONS: {
      const splittedOperations = operations.reduce(
        splitOperationsByType(/(^taxonomy\.|Inventory\.)/),
        { archivedOperations: EMPTY.LIST, filteredOperations: EMPTY.LIST }
      );
      let { archivedOperations } = splittedOperations;
      const { filteredOperations } = splittedOperations;
      const { profile } = data;
      const { value: { taxonomyId: category, title }} = profile;
      let operation = Map({ type: OPERATIONS[channel].VARIATIONS });
      const photos = product.get(FORM.PHOTOS);
      const inventory = product.get(FORM.INVENTORY);
      let variations = shapeVariationsForApp({ category, channel, data: profile.value, inventory, photos });
      let oldVariations = product.getIn([VARIATIONS, 'oldVariations']);

      if (!oldVariations) {
        oldVariations = product.get(VARIATIONS);
      } else {
        archivedOperations = product.getIn([VARIATIONS, 'operations']);
      }

      switch (channel) {
        case ETSY: {
          variations.get(COMBINATIONS).forEach(function forEachCombination(combination, combinationIndex) {
            const negativeId = -1 - combinationIndex;
            variations = variations.setIn([COMBINATIONS, combinationIndex, 'id'], negativeId);
            profile.value.offerings[combinationIndex].id = negativeId;
          });

          if (category !== product.getIn([FORM.DETAILS, CATEGORY, VALUE])) {
            if (!product.getIn([FORM.DETAILS, CATEGORY, 'oldCategory'])) {
              product = product.setIn([FORM.DETAILS, CATEGORY, 'oldCategory'],
                product.getIn([FORM.DETAILS, CATEGORY, VALUE])
              );
            }

            const updates = editDetails({ channel, product, [CATEGORY]: { category }});

            if (updates.confirmation) {
              confirmation = updates.confirmation;
            }

            product = updates.product;
          }

          break;
        }

        case SHOPIFY: {
          operation = operation
            .set('templateId', profile.templateId)
            .set('shopifyMeta', Map(profile.shopifyMeta));

          break;
        }

        default: {
          break;
        }
      }

      operation = operation.set(VALUE, fromJS(profile.value));

      variations = retain({ channel, product: product.set(VARIATIONS, oldVariations), variations })
        .set('oldVariations', oldVariations)
        .set('currentProfile', Map({ profileId, title }));

      product = product
        .set(VARIATIONS, variations.set('operations', archivedOperations))
        .set('operations', filteredOperations.push(operation));

      product = updateProductVariationsErrors({ channel, product });

      break;
    }

    default: {
      break;
    }
  }

  product = setIsComplete(product);

  const products = state.get('products').set(shopId, product);

  return {
    confirmation,
    state: state.set('products', products).set('isComplete', areComplete(products)),
  };
}

export function applyProfileV2({ product: source, profile, template }) {
  const channel = source.get('channel');

  function setForm({ form, product, value }) {
    return product.set(form, value);
  }

  function addOperations({ operations, product }) {
    return product.set('operations', product.get('operations', EMPTY.LIST).concat(operations));
  }

  function replaceOperations({ operations, product }) {
    return product.set('operations', operations.reduce(replaceOperation, product.get('operations', EMPTY.LIST)));
  }

  function reduceForms(result, form) {
    if (!profile.getIn(['sections', form])) return result;

    switch (form) {
      case FORM.DESCRIPTION: {
        const DESCRIPTION_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].DESCRIPTION,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].DESCRIPTION,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: DESCRIPTION_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.DETAILS: {
        const DETAILS_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].WHO_MADE,
            OPERATIONS[ETSY].IS_SUPPLY,
            OPERATIONS[ETSY].WHEN_MADE,
            OPERATIONS[ETSY].CATEGORY,
            OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES,
            OPERATIONS[ETSY].SHOULD_AUTO_RENEW,
            OPERATIONS[ETSY].DIGITAL,
            OPERATIONS[ETSY].PRODUCTION_PARTNERS,
            OPERATIONS[ETSY].SECTION,
            OPERATIONS[ETSY].MATERIALS.CHANGE_TO,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].PRODUCT_TYPE,
            OPERATIONS[SHOPIFY].VENDOR,
            OPERATIONS[SHOPIFY].COLLECTIONS,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: DETAILS_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.FILES: {
        const FILES_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].FILES.ADD,
          ],
        };

        if (!FILES_OPERATIONS.hasOwnProperty(channel)) break;

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: FILES_OPERATIONS,
          product: template,
        });

        result.product = addOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.INVENTORY: {
        const INVENTORY_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].QUANTITY,
            OPERATIONS[ETSY].SKU,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].VARIATIONS,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: INVENTORY_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.PERSONALIZATION: {
        const PERSONALIZATION_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET,
            OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET,
            OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET,
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET,
          ],
        };

        if (!PERSONALIZATION_OPERATIONS.hasOwnProperty(channel)) break;

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: PERSONALIZATION_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.PHOTOS: {
        const PHOTOS_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].PHOTOS.CHANGE_TO,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].PHOTOS.CHANGE_TO,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: PHOTOS_OPERATIONS,
          product: template,
        });

        result.product = addOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.PRICING: {
        const PRICING_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].PRICE,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].VARIATIONS,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: PRICING_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.SEO: {
        const SEO_OPERATIONS = {
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].META_DESCRIPTION,
            OPERATIONS[SHOPIFY].PAGE_TITLE,
          ],
        };

        if (!SEO_OPERATIONS.hasOwnProperty(channel)) break;

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: SEO_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.SHIPPING: {
        const SHIPPING_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].SHIPPING_PROFILE,
            OPERATIONS[ETSY].WEIGHT,
            OPERATIONS[ETSY].LENGTH,
            OPERATIONS[ETSY].WIDTH,
            OPERATIONS[ETSY].HEIGHT,
            OPERATIONS[ETSY].RETURN_POLICY,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].VARIATIONS,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: SHIPPING_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.TAGS: {
        const TAGS_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].TAGS.CHANGE_TO,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].TAGS.CHANGE_TO,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: TAGS_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.TITLE: {
        const TITLE_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].TITLE,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].TITLE,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: TITLE_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      case FORM.VARIATIONS: {
        if (
          channel === ETSY &&
          template.getIn([FORM.DETAILS, CATEGORY, VALUE]) !== result.product.getIn([FORM.DETAILS, CATEGORY, VALUE])
        ) {
          const category = template.getIn([FORM.DETAILS, CATEGORY, VALUE]);
          const updates = editDetails({ channel, product: result.product, [CATEGORY]: { category }});

          if (updates.confirmation) {
            result.confirmation = updates.confirmation;
          }

          result.product = updates.product;
        }
        const VARIATIONS_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].VARIATIONS,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].VARIATIONS,
          ],
        };

        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: VARIATIONS_OPERATIONS,
          product: template,
        });

        result.product = replaceOperations({
          operations,
          product: setForm({
            form,
            product: result.product,
            value: retain({ channel, product: result.product, variations: template.get(form) }),
          }),
        });

        break;
      }

      case FORM.VIDEOS: {
        const VIDEOS_OPERATIONS = {
          [ETSY]: [
            OPERATIONS[ETSY].VIDEOS.ADD,
          ],
          [SHOPIFY]: [
            OPERATIONS[SHOPIFY].VIDEOS.ADD,
          ],
        };
        const operations = getOperationsFromProduct({
          archived: result.product,
          channel,
          operationTypes: VIDEOS_OPERATIONS,
          product: template,
        });

        result.product = addOperations({
          operations,
          product: setForm({ form, product: result.product, value: template.get(form) }),
        });

        break;
      }

      default: {
        break;
      }
    }

    return result;
  }

  const { confirmation, product } = FORMS[channel].reduce(reduceForms, { product: source });

  return {
    confirmation,
    product: product.has(ERROR)
      ? setProductErrors({ product })
      : product,
  };
}
